import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Trash2, ShoppingCart as CartIcon } from 'lucide-react';

const MyCart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart } = useAuth();
  const [removing, setRemoving] = useState(null);

  const handleRemove = async (projectId) => {
    setRemoving(projectId);
    try {
      await removeFromCart(projectId);
    } catch (error) {
      alert('Erro ao remover item do carrinho');
    } finally {
      setRemoving(null);
    }
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.project_price, 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    
    // Redirecionar para checkout com o carrinho
    navigate('/checkout', { state: { fromCart: true } });
  };

  if (cart.length === 0) {
    return (
      <Card className="p-12 text-center">
        <CartIcon size={64} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Seu carrinho está vazio</h3>
        <p className="text-gray-500 mb-6">Explore nossos projetos e adicione ao carrinho!</p>
        <Button
          onClick={() => navigate('/')}
          className="bg-[#00A86B] hover:bg-[#008c5a] text-white"
        >
          Ver Projetos
        </Button>
      </Card>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Meu Carrinho</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.map((item) => (
            <Card key={item.project_id} className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-800">{item.project_name}</h3>
                  <div className="mt-2 space-y-1">
                    {item.selected_services.includes('arquitetonico') && (
                      <p className="text-sm text-gray-600">• Projeto Arquitetônico</p>
                    )}
                    {item.selected_services.includes('interiores') && (
                      <p className="text-sm text-gray-600">• Projeto de Interiores</p>
                    )}
                    {item.selected_services.includes('rrt') && (
                      <p className="text-sm text-gray-600">• RRT + Assessoria Técnica</p>
                    )}
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className="font-bold text-xl text-[#00A86B]">R$ {formatCurrency(item.project_price)}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(item.project_id)}
                    disabled={removing === item.project_id}
                    className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Remover
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <Card className="p-6 sticky top-24">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Resumo</h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-gray-600">
                <span>Itens ({cart.length})</span>
                <span>R$ {formatCurrency(calculateTotal())}</span>
              </div>
              
              <div className="flex justify-between text-xl font-bold text-gray-800 pt-3 border-t">
                <span>Total:</span>
                <span>R$ {formatCurrency(calculateTotal())}</span>
              </div>

              <div className="text-sm text-gray-600 text-center pt-2">
                em até 12x sem juros no cartão
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold"
            >
              Finalizar Compra
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyCart;